import { useMemo } from 'react';

import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EPublisherSolutionModelSubType } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';

import { cn } from 'components/lib/utils';

import { usePageTypeByFeatureFlag } from '../../../hooks/usePageTypeByFF';
import { AssetsLibrary, projectTypeRestrictions } from '../constants';
import { useSidebar } from '../context';
import { SidebarMenuButton } from '../elements';
import { useSidebarUrl } from '../hooks';
import {
  ESideBarChipType,
  ESideBarContext,
  ESidebarGroupKey,
  SideBarItem
} from '../types';

import SidebarMenuItemChip from './SidebarMenuItemChip';

interface SidebarItemProps {
  item: SideBarItem;
  groupTitle?: string;
}

export const SidebarMenuItemComponent = ({
  item,
  groupTitle
}: SidebarItemProps) => {
  const { context, isCheckout, userDetails, currentProjectSubType } =
    useSidebar();
  const location = useLocation();
  const { getNavFullUrl } = useSidebarUrl(context);
  const { pageType } = usePageTypeByFeatureFlag(item.title);

  // Determine if item should be disabled
  const { isDisabled, chip, conditionalOnClick } = useMemo(() => {
    let isDisabled = item?.isDisabled;
    let chip = item?.chip;
    let conditionalOnClick;
    const seenTabs = JSON.parse(
      localStorageUtil.getAny(ELocalStorageKeys.NEW_MENU_TABS_SEEN) || '[]'
    );

    if (pageType && !seenTabs.includes(item.title)) {
      chip = pageType;
    }

    // Handle checkout restrictions
    if (isCheckout && context === ESideBarContext.PROJECT) {
      const publisherId = location.pathname
        .split('/project/')[1]
        ?.split('/')[0];
      const currentProject = userDetails?.projects?.find(
        (project) => project.publisherId === publisherId
      );

      if (currentProject?.projectType === 'independentCheckout' && groupTitle) {
        const disabledItems =
          projectTypeRestrictions.independentCheckout.disabledItems[
            groupTitle as ESidebarGroupKey
          ];

        const isCurrentItemDisabled = disabledItems?.some(
          (disabledItem: SideBarItem) => disabledItem.title === item.title
        );

        if (
          isCurrentItemDisabled &&
          !(
            currentProjectSubType ===
              EPublisherSolutionModelSubType.PAYMENT_LINKS &&
            item.title === AssetsLibrary.title
          )
        ) {
          isDisabled = true;
        }
      }
    }

    if (chip === ESideBarChipType.NEW) {
      conditionalOnClick = () => {
        if (!seenTabs.includes(item.title)) {
          seenTabs.push(item.title);
          localStorageUtil.set(
            ELocalStorageKeys.NEW_MENU_TABS_SEEN,
            JSON.stringify(seenTabs)
          );
        }
      };
    }

    if (chip === ESideBarChipType.COMING_SOON && item.docsLink) {
      conditionalOnClick = () => {
        window.open(item.docsLink, '_blank');
      };
    }

    return { isDisabled, chip, conditionalOnClick };
  }, [
    item?.isDisabled,
    item?.chip,
    item.title,
    item.docsLink,
    pageType,
    isCheckout,
    context,
    location.pathname,
    userDetails?.projects,
    groupTitle,
    currentProjectSubType
  ]);

  const navUrl = getNavFullUrl(item, groupTitle as ESidebarGroupKey);

  const basicContent = (
    <>
      {typeof item.icon === 'string' ? (
        <FontAwesomeIcon className="p-3" icon={cn('fa-regular', item.icon)} />
      ) : (
        item.icon
      )}
      <span className="flex-1 min-w-0 truncate">{item.title}</span>
      <SidebarMenuItemChip type={chip} isDisabled={isDisabled} />
    </>
  );

  return (
    <SidebarMenuButton
      asChild
      isActive={location.pathname.includes(navUrl)}
      isDisabled={isDisabled}
      className="group/sidebar-menu-btn"
    >
      {isDisabled ? (
        <div className="flex items-center gap-2">{basicContent}</div>
      ) : (
        <Link to={navUrl} onClick={conditionalOnClick}>
          {basicContent}
        </Link>
      )}
    </SidebarMenuButton>
  );
};
